<!--
  The base Dialog component, used to perform quick actions, useful for when
  navigating to a new page would lose the current context.
-->
<script lang="ts">
export interface XDialogProps {
  // Name used for tracking
  name: string

  // To enable closing a dialog when clicking the blur area.
  closeOnBlurClick?: boolean

  // To avoid displaying a close button on the top right corner.
  disableClose?: boolean

  // Shows a loading indicator next to the title until the Promise is resolved.
  loadingUntil?: Promise<any>

  // Controls the dialog width.
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'

  // Alignment for the footer.
  align?: 'center' | 'end'
}
</script>

<script setup lang="ts">
import { useCurrentElement, useWindowSize } from '@vueuse/core'
import { useFocusTrap } from '@vueuse/integrations/useFocusTrap'
import { useCloseDialog } from '@corp/composables/dialogs'
import { onEscape } from '@corp/composables/keyboard'
import { useListeners } from '@shared/vue'
import { isFunction } from '@shared/functions'
import { trackModal } from '@shared/tracking'

const { name, size = 'md', align = 'end' } = defineProps<XDialogProps>()
const emit = defineEmits(['close'])
const notifyCloseDialog = useCloseDialog()

const hasCloseListener = useListeners(listeners => isFunction(listeners.onClose))

const { height } = $(useWindowSize())

const dialogContainerEl = $ref<HTMLElement>()

// Adjusts the dialog vertically based on the current screen height.
watchEffect(() => {
  if (!dialogContainerEl) return
  const elHeight = parseInt(getComputedStyle(dialogContainerEl, null).getPropertyValue('height'))
  dialogContainerEl.style.marginTop = `${Math.max(0, (height - elHeight) / 5)}px`
})

// Emit an event to notify that the user intends to close the dialog.
//
// NOTE: When the parent component adds a @close listener, it must
// use notifyCloseDialog manually to close the dialog.
function stopAndCloseDialog (event: Event) {
  hasCloseListener ? emit('close', event) : notifyCloseDialog(event)
}

// 'reset' is emitted by form elements when CancelButton is clicked.
//
// NOTE: By leveraging the native 'reset' event, there is no need to manually
// add a close listener on every dialog forms.
function onFormReset (event: Event) {
  stopAndCloseDialog(event)
}

const sizes = {
  sm: '400px',
  md: '470px',
  lg: '540px',
  xl: '896px',
  '2xl': '1128px',
  auto: 'auto', // Allow the content set the width
}

const dialogWidth = $computed(() => sizes[size])

const footerStyles = $computed(() => ([
  align === 'end' ? 'justify-end' : 'justify-center',
  size === 'sm' || size === 'md' ? 'mt-6' : 'mt-8',
]))

useFocusTrap(useCurrentElement<HTMLElement>(), {
  immediate: true,
  initialFocus: false,
  escapeDeactivates: false,
})

onEscape(stopAndCloseDialog)

onMounted(() => trackModal(name))
</script>

<template>
  <XBlurArea
    class="dialog break-word grid py-8 content-start justify-center text-xs z-dialog"
    mask
    @blur="closeOnBlurClick && stopAndCloseDialog($event)"
  >
    <div
      ref="dialogContainerEl"
      class="dialog-box rounded-md bg-white transition drop-shadow-md py-9 relative"
      :class="size === 'xl' || size === '2xl' ? 'px-9' : 'px-6'"
      @reset="onFormReset"
    >
      <header class="text-md text-center font-black">
        <slot name="dialogHeader"/>
        <XPromiseIndicator v-if="loadingUntil" tabindex="0" :loadingUntil="loadingUntil"/>
        <XButton
          :disabled="disableClose"
          tabindex="-1"
          icon="close"
          name="Close Dialog"
          aria-label="Close Dialog"
          class="absolute not-hover:opacity-50 top-2 right-2"
          @click="stopAndCloseDialog"
        />
      </header>
      <slot name="dialogContent">
        <main class="text-center text-sm mt-4">
          <slot name="dialogBody"/>
        </main>
        <footer v-if="$slots.dialogFooter" class="flex flex-wrap gap-4" :class="footerStyles">
          <slot name="dialogFooter"/>
        </footer>
      </slot>
    </div>
  </XBlurArea>
</template>

<style lang="scss" scoped>
.dialog {
  grid-template-columns: min(v-bind("dialogWidth"), 90%);
}

:slotted(p) {
  @apply text-xs font-normal;
}

header > :deep(.icon:first-child) {
  @apply align-text-top mr-2;
}
</style>
